import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Palmtree, Heart, GraduationCap, Calendar, Trash2, CalendarDays } from 'lucide-react';

/**
 * Absence Dialog for creating/editing absences (vacation, sick, training)
 */
const AbsenceDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  employees,
  isSubmitting,
  isEditing,
  editingAbsence,
  onSubmit,
  onDelete
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Abwesenheit bearbeiten' : 'Neue Abwesenheit'}
          </DialogTitle>
          <DialogDescription>
            Urlaub, Krankheit oder andere Abwesenheiten erfassen
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Mitarbeiter *</Label>
            <Select 
              value={form.user_id} 
              onValueChange={(v) => setForm(prev => ({ ...prev, user_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Mitarbeiter wählen..." />
              </SelectTrigger>
              <SelectContent>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Art der Abwesenheit</Label>
            <Select 
              value={form.absence_type} 
              onValueChange={(v) => setForm(prev => ({ ...prev, absence_type: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vacation">
                  <span className="flex items-center gap-2">
                    <Palmtree className="w-4 h-4 text-green-600" />
                    Urlaub
                  </span>
                </SelectItem>
                <SelectItem value="sick">
                  <span className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-600" />
                    Krankheit
                  </span>
                </SelectItem>
                <SelectItem value="training">
                  <span className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-blue-600" />
                    Weiterbildung
                  </span>
                </SelectItem>
                <SelectItem value="holiday">
                  <span className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-purple-600" />
                    Feiertag
                  </span>
                </SelectItem>
                <SelectItem value="other">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    Sonstige
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Von *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                required
              />
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.half_day_start}
                  onChange={(e) => setForm(prev => ({ ...prev, half_day_start: e.target.checked }))}
                  className="rounded"
                />
                Halber Tag (ab Mittag)
              </label>
            </div>
            <div>
              <Label>Bis *</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                required
              />
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.half_day_end}
                  onChange={(e) => setForm(prev => ({ ...prev, half_day_end: e.target.checked }))}
                  className="rounded"
                />
                Halber Tag (bis Mittag)
              </label>
            </div>
          </div>

          <div>
            <Label>Notizen</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              placeholder="Optionale Bemerkungen..."
            />
          </div>

          <DialogFooter className="flex justify-between">
            {isEditing && editingAbsence && onDelete && (
              <Button 
                type="button" 
                variant="destructive" 
                onClick={() => onDelete(editingAbsence.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Speichern
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AbsenceDialog;
